/**
 * Auth 上下文 —— 包装 Supabase Auth 的 React hook
 *
 * 暴露：
 *   const { user, session, loading, signInWithEmail, signInWithOAuth, signOut } = useAuth();
 *
 * 当 Supabase 未配置时，user/session 永远是 null，所有 sign* 方法 throw，
 * 让 UI 自然把登录入口隐藏。
 *
 * v0.3 起：supabase 客户端是懒加载的（getSupabase()），AuthProvider 内异步初始化。
 *   → 未登录访客访问 / 时不会下载 supabase-js（~150KB gzip）
 */
import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { getSupabase, isSupabaseEnabled } from './supabase.js';

const AuthCtx = createContext({
  user: null,
  session: null,
  loading: true,
  isSupabaseEnabled: false,
  signInWithEmail: async () => { throw new Error('auth_disabled'); },
  signInWithOAuth: async () => { throw new Error('auth_disabled'); },
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseEnabled) {
      setLoading(false);
      return;
    }
    let alive = true;
    let unsub;
    (async () => {
      const sb = await getSupabase();
      if (!alive || !sb) {
        if (alive) setLoading(false);
        return;
      }
      const { data: sd } = await sb.auth.getSession();
      if (!alive) return;
      setSession(sd?.session || null);
      setLoading(false);
      const { data } = sb.auth.onAuthStateChange((_event, s) => {
        if (alive) setSession(s || null);
      });
      unsub = () => data?.subscription?.unsubscribe?.();
    })();
    return () => {
      alive = false;
      unsub?.();
    };
  }, []);

  const signInWithEmail = useCallback(async (email) => {
    const sb = await getSupabase();
    if (!sb) throw new Error('auth_disabled');
    const redirectTo = `${window.location.origin}/auth/callback`;
    const { error } = await sb.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: redirectTo, shouldCreateUser: true },
    });
    if (error) throw error;
  }, []);

  const signInWithOAuth = useCallback(async (provider) => {
    const sb = await getSupabase();
    if (!sb) throw new Error('auth_disabled');
    const redirectTo = `${window.location.origin}/auth/callback`;
    const { error } = await sb.auth.signInWithOAuth({
      provider,
      options: { redirectTo },
    });
    if (error) throw error;
  }, []);

  const signOut = useCallback(async () => {
    const sb = await getSupabase();
    if (!sb) return;
    await sb.auth.signOut();
  }, []);

  const value = useMemo(
    () => ({
      user: session?.user || null,
      session,
      loading,
      isSupabaseEnabled,
      signInWithEmail,
      signInWithOAuth,
      signOut,
    }),
    [session, loading, signInWithEmail, signInWithOAuth, signOut],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}
